/**
 * Dev mode contract (shared between server, client and the Device Cow agent).
 *
 * Dev mode exists ONLY in development environments: the main dev deployment
 * (`aquaduck`) and PR previews (`aquaduck-pr-N`). Production-type environments
 * omit it entirely (server: `AQUADUCK_DEV_MODE` unset → every `/api/dev` route
 * and `/dev/term` socket returns 404; client: `GET /api/dev/env` says
 * `devMode:false` → no Dev tab is rendered).
 *
 * Dev mode replaces the Aquaduck agent with the Device Cow coordinator agent
 * inside a separate "Dev Chat", adds a development console (Dev Chat and PR
 * preview cards) and lets Device Cow dispatch Codex subagents that run in
 * PTYs inside the environment's own pod.
 *
 * Everything durable lives in the SAME Yjs doc as the workspace (so it is per
 * environment: each PR preview has its own schema and therefore its own Dev
 * Chat and agent list). Ephemeral terminal I/O goes over a dedicated websocket
 * (`DEV_TERM_PATH`).
 */

import type * as Y from "yjs";
import type { ChatMessage } from "./model.js";
import type { DevcowRequest } from "./devcow-api.js";

/** Server-written delivery state in the originating workspace's devCoordinator map. */
export const commentDevelopmentKey = (commentId: string) => `commentRequest:${commentId}`;
export interface CommentDevelopmentRequest {
  status: "pending" | "sent" | "failed";
  /** Immutable handoff snapshot: retries must use the same idempotency inputs. */
  request: DevcowRequest;
  requestId?: string;
  url?: string;
}

// ------------------------------------------------------------- identity

/**
 * The Device Cow coordinator identity. A normal user in every respect
 * (UserRecord with isAgent:true, chat bubbles, @-mentionable, presence in the
 * online list) EXCEPT it never publishes a cursor (`cursor: null` always) and
 * never appears on the canvas.
 */
export const DEVCOW_USER = {
  id: "devcow",
  name: "Device Cow",
  colorId: "cow",
  avatarUrl: "/device-cow-logo.png",
} as const;

/** How Device Cow is mentioned in Dev Chat: `@Device Cow` (case-insensitive). Its reserved color is `DEVCOW_COLOR` in colors.ts. */
export const DEVCOW_MENTION = "@Device Cow";

/**
 * The PR Preview Coordinator identity for preview N (tier 2, see
 * docs/devicecow_platform.md). One per preview, hosted by the devicecow
 * deployment; it owns that preview's Dev Chat. Like Device Cow it never
 * publishes a cursor. `@Device Cow` always refers to the single tier-1 agent.
 */
export const PREVIEW_COORDINATOR_ID_PREFIX = "devcow-pr-";
export const PREVIEW_COORDINATOR_AVATAR_URL = "/pr-coordinator-logo.png";
export const previewCoordinatorUser = (pr: number) => ({
  id: `${PREVIEW_COORDINATOR_ID_PREFIX}${pr}`,
  name: `PR ${pr} Coordinator`,
  colorId: "cow",
  avatarUrl: PREVIEW_COORDINATOR_AVATAR_URL,
} as const);
export const isCoordinatorUserId = (id: string) => id === DEVCOW_USER.id || id.startsWith(PREVIEW_COORDINATOR_ID_PREFIX);

// ------------------------------------------------------------ doc keys

/** Additional top-level shared types in the workspace Y.Doc. */
export const DevKeys = {
  /** Y.Array<ChatMessage>: the Dev Chat (humans + Device Cow). Same message shape as `chat`. */
  devChat: "devChat",
  /** Y.Map<DevAgent>: running/finished subagents, key = agentId. Server-owned; clients only read. */
  devAgents: "devAgents",
  /** Y.Map<unknown>: Device Cow's durable coordinator state (see DevCoordinatorFields). Server-owned. */
  devCoordinator: "devCoordinator",
} as const;

// --------------------------------------------------------------- agents

export type DevAgentStatus =
  | "starting" // PTY spawned, codex booting
  | "running" // codex is working (recent output)
  | "idle" // codex is waiting for input / finished its turn (no output for a while)
  | "exited" // process exited on its own (see exitCode)
  | "killed" // terminated by a user or Device Cow
  | "lost"; // pod restarted; the PTY no longer exists

/** Plain JSON value stored in `devAgents`. Written by the server only. */
export interface DevAgent {
  id: string;
  /** Short human title Device Cow gave the task, e.g. "lane: dev-client". */
  title: string;
  status: DevAgentStatus;
  /** Repo-relative path (in the pod) of the prompt file the agent was launched with. */
  promptPath: string;
  /** Codex thread id bound to this worker's PTY; used for `codex resume`. */
  threadId?: string;
  /** Absolute working directory of the agent (its git worktree in the pod). */
  cwd: string;
  /** Git branch the agent commits to (e.g. `agent/<id>`), pushed to origin. */
  branch?: string;
  startedAt: number;
  lastOutputAt?: number;
  exitedAt?: number;
  exitCode?: number;
  /** Device Cow's latest one-line summary of what the agent is doing / did. */
  summary?: string;
  /** Always DEVCOW_USER.id: users cannot create agents directly. */
  createdBy: string;
}

// ---------------------------------------------------------- coordinator

/**
 * Durable state of the Device Cow coordinator so its session survives pod
 * restarts. Field names inside the `devCoordinator` Y.Map.
 */
export const DevCoordinatorFields = {
  /** OpenAI Responses API id to chain from (`previous_response_id`). */
  previousResponseId: "previousResponseId",
  /** Most recent compaction summary (handoff notes) used to seed a fresh chain. */
  compactSummary: "compactSummary",
  /** Epoch ms of the last compaction. */
  compactedAt: "compactedAt",
  /** Running estimate of the chain's input tokens (from usage). */
  inputTokens: "inputTokens",
  /** Epoch ms of the last periodic status report posted to Dev Chat. */
  lastReportAt: "lastReportAt",
  /** Server boot id that last owned this coordinator (detects restarts). */
  bootId: "bootId",
  /**
   * Legacy JSON dictionary. New writers migrate once to previewRequestsById,
   * a Y.Map<Y.Map<unknown>> with one record per PR; shared readers prefer it.
   * Fields include { issue: number; issueUrl: string; prTitle: string;
   * requestedBy: string; request: string; threadMessageId: string; createdAt: number } — the request
   * each preview was opened for; replies in that thread route to the PR coordinator.
   */
  previewRequests: "previewRequests",
  /** Codex thread id of this coordinator's shell, resumed with `codex resume` after a restart. */
  threadId: "threadId",
} as const;

// ------------------------------------------------------------------ API

/** `GET /api/dev/env` → DevEnvInfo. The ONLY dev route that also exists when dev mode is off (returns devMode:false). */
export const DEV_ENV_PATH = "/api/dev/env";
export interface DevEnvInfo {
  devMode: boolean;
  /** "main" = the main dev deployment; "pr" = a PR preview. */
  env: "main" | "pr" | null;
  /** PR number when env === "pr". */
  pr: number | null;
  /** Git branch this environment runs (PR head branch, or "main"). */
  branch: string | null;
  /** Deployed source sha. */
  sha: string | null;
  /** Public URL of this environment. */
  url: string;
  /** GitHub repo `owner/name`. */
  repo: string;
}

/** `GET /api/dev/environments` → { environments: DevEnvironment[] } (from the k8s API: running pods labelled app.kubernetes.io/instance). */
export const DEV_ENVIRONMENTS_PATH = "/api/dev/environments";
export interface DevEnvironment {
  /** app.kubernetes.io/instance: `aquaduck` or `aquaduck-pr-<N>`. */
  name: string;
  kind: "main" | "pr";
  pr: number | null;
  /** `https://pr-<N>.in.aquaduck.dev (main: https://in.aquaduck.dev)` */
  url: string;
  /** GitHub PR URL for kind === "pr". */
  prUrl: string | null;
  ready: boolean;
  prState?: "open" | "closed" | "merged";
  prLabels?: string[];
  readyForReview?: boolean;
  /** GitHub metadata, independent of coordinator readiness and merge opt-in. */
  prDraft?: boolean;
  /** null means GitHub is still calculating; absent means unavailable. */
  prMergeable?: boolean | null;
  prMergeState?: string;
  sha: string | null;
  startedAt: string | null;
  /** True when this is the environment serving the request. */
  current: boolean;
}

/** `POST /api/dev/previews` → DevCreatePreviewResponse. Opens a PR with a single empty commit; the preview workflow deploys it. */
export const DEV_PREVIEWS_PATH = "/api/dev/previews";
export interface DevCreatePreviewResponse {
  prState?: "open" | "closed" | "merged";
  prLabels?: string[];
  readyForReview?: boolean;
  pr: number;
  prUrl: string;
  branch: string;
  /** Expected preview URL (may take minutes to become ready). */
  url: string;
}

/** `DELETE /api/dev/agents/:id` → { ok: true }. Kills the subagent's process (status → killed). */
export const devAgentPath = (agentId: string) => `/api/dev/agents/${encodeURIComponent(agentId)}`;

/**
 * Terminal websocket: `ws(s)://<host>/dev/term/<agentId>`.
 * Frames:
 *  - server → client, binary: raw PTY output bytes (UTF-8). On connect the
 *    server first sends the retained scrollback (up to DEV_TERM_SCROLLBACK_BYTES)
 *    as one binary frame, then live output.
 *  - client → server, binary: raw keyboard input bytes for the PTY.
 *  - either direction, text (JSON): control messages, see DevTermControl.
 */
export const DEV_TERM_PATH = "/dev/term";
export const devTermPath = (agentId: string) => `${DEV_TERM_PATH}/${encodeURIComponent(agentId)}`;
export const DEV_TERM_SCROLLBACK_BYTES = 2560 * 1024;
/** Room for retained history plus live output while a viewer catches up. */
export const DEV_TERM_BUFFER_BYTES = DEV_TERM_SCROLLBACK_BYTES + 1024 * 1024;
export type DevTermControl =
  | { type: "resize"; cols: number; rows: number } // client → server
  | { type: "exit"; code: number | null } // server → client
  | { type: "error"; message: string }; // server → client

// -------------------------------------------------------------- helpers

export function getDevChat(doc: Y.Doc): Y.Array<ChatMessage> {
  return doc.getArray<ChatMessage>(DevKeys.devChat);
}
export function getDevAgents(doc: Y.Doc): Y.Map<DevAgent> {
  return doc.getMap<DevAgent>(DevKeys.devAgents);
}
export function getDevCoordinator(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>(DevKeys.devCoordinator);
}
